import Phaser from "phaser";
import { getSelectedLevel, setSelectedLevel } from "../core/levels/LevelSelection";
import { listLevels, MIN_LEVEL, MAX_LEVEL } from "../core/levels/LevelFactory";

export type LevelSelectOverlay = {
  destroy: () => void;
  layout: (cx: number, cy: number, scaleFactor: number) => void;
  getLevel: () => number;
  getBottomY: () => number;
};

type Styles = {
  fontFamily: string;
  fontSize: number;
  color: string;
  stroke: string;
  strokeThickness: number;
};

export function createLevelSelectOverlay(scene: Phaser.Scene): LevelSelectOverlay {
  // Pre-load level metadata once; used for label blurbs later if you want.
  const levels = listLevels();

  let current = getSelectedLevel();

  const root = scene.add.container(0, 0);

  const styles: Styles = {
    fontFamily: "Arial Black",
    fontSize: 28,
    color: "#ffffff",
    stroke: "#000000",
    strokeThickness: 8,
  };

  const label = scene.add
    .text(0, 0, "Start Level", styles)
    .setOrigin(0.5, 0.5);

  const left = scene.add
    .text(0, 0, "◀", styles)
    .setOrigin(0.5, 0.5)
    .setInteractive({ useHandCursor: true });

  const value = scene.add
    .text(0, 0, String(current), styles)
    .setOrigin(0.5, 0.5);

  const right = scene.add
    .text(0, 0, "▶", styles)
    .setOrigin(0.5, 0.5)
    .setInteractive({ useHandCursor: true });

  const blurb = scene.add
    .text(0, 0, levels.find((l) => l.id === current)?.blurb ?? "", {
      fontFamily: "monospace",
      fontSize: "18px",
      color: "#cfcfe6",
      wordWrap: { width: 560 },
      align: "center",
    })
    .setOrigin(0.5, 0);

  const update = () => {
    value.setText(String(current));
    const b = levels.find((l) => l.id === current)?.blurb ?? "";
    blurb.setText(b);
  };

  const bump = (delta: number) => {
    current = setSelectedLevel(current + delta);
    update();
  };

  left.on("pointerdown", (p: Phaser.Input.Pointer) => {
    p.event.stopPropagation();
    bump(-1);
  });

  right.on("pointerdown", (p: Phaser.Input.Pointer) => {
    p.event.stopPropagation();
    bump(1);
  });

  root.add([label, left, value, right, blurb]);

  const overlay: LevelSelectOverlay = {
    destroy: () => root.destroy(true),
    layout: (cx: number, cy: number, scaleFactor: number) => {
      // Scale the big items together
      const s = Math.max(0.75, Math.min(1.2, scaleFactor));
      label.setScale(s);
      left.setScale(s);
      value.setScale(s);
      right.setScale(s);

      const gap = 70 * s;
      label.setPosition(cx, cy);
      left.setPosition(cx - gap, cy + 52 * s);
      value.setPosition(cx, cy + 52 * s);
      right.setPosition(cx + gap, cy + 52 * s);

      // Blurb below
      blurb.setPosition(cx, cy + 90 * s);
      blurb.setWordWrapWidth(Math.min(560, scene.scale.width * 0.86));
    },
    getLevel: () => current,
  };

  // Clamp and ensure we never store out-of-range
  if (current < MIN_LEVEL || current > MAX_LEVEL) {
    current = setSelectedLevel(current);
    update();
  }

  update();
  return overlay;
}
